import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  documentId,
  onSnapshot,
  arrayRemove
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import {
  UserProfile,
  Study,
  Award,
  Organization,
  Course,
  CourseMetrics,
  Question,
  QuestionReply,
  MediaItem,
  MediaType,
  CTAType,
  CTAResponse,
  CTAResponseData,
  CTAInteraction
} from '../types';

// Helper for safe environment variable access
const getEnv = (key: string): string | undefined => {
  // @ts-ignore
  return import.meta.env?.[key];
};

// Configuration
// Using Vite environment variables (VITE_ prefix)
// Fallback to placeholder to trigger mock mode if keys are missing
const firebaseConfig = {
  apiKey: getEnv('VITE_FIREBASE_API_KEY') || "AIzaSy_PLACEHOLDER",
  authDomain: getEnv('VITE_FIREBASE_AUTH_DOMAIN') || "placeholder.firebaseapp.com",
  projectId: getEnv('VITE_FIREBASE_PROJECT_ID') || "placeholder-id",
  storageBucket: getEnv('VITE_FIREBASE_STORAGE_BUCKET') || "placeholder.appspot.com",
  messagingSenderId: getEnv('VITE_FIREBASE_MESSAGING_SENDER_ID') || "123456789",
  appId: getEnv('VITE_FIREBASE_APP_ID') || "1:123456789:web:abcdef"
};

// Check if we are in a mock environment (missing or placeholder keys)
let isMock = !firebaseConfig.apiKey || firebaseConfig.apiKey.includes("PLACEHOLDER");

let app: FirebaseApp;
let auth: any;
let db: any;
let storage: any;

if (!isMock) {
  try {
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApp();
    }
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
  } catch (e) {
    console.warn("Failed to initialize Firebase, falling back to mock mode", e);
    // @ts-ignore
    isMock = true;
  }
}

// --- MOCK STATE ---
const mockDb = {
  users: {} as Record<string, UserProfile>,
  studies: [] as Study[],
  organizations: [] as Organization[],
  studyMetrics: [] as import('../types').StudyMetrics[],
  courses: [] as Course[],
  courseMetrics: [] as CourseMetrics[]
};

// Initialize some mock data
if (isMock) {
  try {
     const storedStudies = sessionStorage.getItem('mock_studies');
     if (storedStudies) mockDb.studies = JSON.parse(storedStudies);

     const storedUsers = sessionStorage.getItem('mock_users');
     if (storedUsers) mockDb.users = JSON.parse(storedUsers);

     const storedOrgs = sessionStorage.getItem('mock_orgs');
     if (storedOrgs) mockDb.organizations = JSON.parse(storedOrgs);

     const storedCourses = sessionStorage.getItem('mock_courses');
     if (storedCourses) mockDb.courses = JSON.parse(storedCourses);
  } catch(e) {}
}

const saveMockData = () => {
  if (!isMock) return;
  sessionStorage.setItem('mock_studies', JSON.stringify(mockDb.studies));
  sessionStorage.setItem('mock_users', JSON.stringify(mockDb.users));
  sessionStorage.setItem('mock_orgs', JSON.stringify(mockDb.organizations));
  sessionStorage.setItem('mock_courses', JSON.stringify(mockDb.courses));
};

// --- EMAIL QUEUE HELPER ---
// Queues an email event to be processed by Cloud Functions
// This is more efficient than triggering on every user document update
type EmailQueueEventType = 'studyCompletion' | 'courseCompletion' | 'welcome';

interface QueueEmailParams {
  type: EmailQueueEventType;
  userId: string;
  userEmail: string;
  userName?: string;
  orgId: string;
  studyId?: string;
  courseId?: string;
}

const queueEmail = async (params: QueueEmailParams): Promise<void> => {
  if (isMock) {
    console.log('[Mock] Email queued:', params);
    return;
  }

  try {
    await addDoc(collection(db, 'emailQueue'), {
      ...params,
      createdAt: new Date()
    });
    console.log('[queueEmail] Successfully added document');
  } catch (error) {
    // Log but don't throw - email failures shouldn't break the main flow
    console.error('Failed to queue email:', error);
  }
};

// --- AUTH SERVICES ---

export const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
  if (isMock) {
    const checkUser = () => {
      const stored = sessionStorage.getItem('mock_auth_user');
      if (stored) {
        callback(JSON.parse(stored) as User);
      } else {
        callback(null);
      }
    };
    
    checkUser();
    
    const listener = () => checkUser();
    window.addEventListener('mock-auth-change', listener);
    return () => window.removeEventListener('mock-auth-change', listener);
  }
  
  return onAuthStateChanged(auth, callback);
};

export const signInWithGoogle = async () => {
  if (isMock) {
    console.log("Using Mock Auth (Google)");
    const mockUser = {
      uid: "mock-user-" + Math.floor(Math.random() * 1000),
      displayName: "Guest User",
      email: "guest@example.com",
      photoURL: null,
      emailVerified: true
    };
    sessionStorage.setItem('mock_auth_user', JSON.stringify(mockUser));
    
    // Ensure profile
    await ensureUserProfile(mockUser as unknown as User);
    
    window.dispatchEvent(new Event('mock-auth-change'));
    return mockUser as unknown as User;
  }

  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    await ensureUserProfile(result.user);
    return result.user;
  } catch (error) {
    console.error("Firebase Auth Error", error);
    throw error;
  }
};

export const registerWithEmail = async (name: string, email: string, password: string) => {
  if (isMock) {
    console.log("Using Mock Auth (Register Email)");
    const mockUser = {
      uid: "mock-user-" + Math.floor(Math.random() * 10000),
      displayName: name,
      email: email,
      photoURL: null,
      emailVerified: false
    };
    sessionStorage.setItem('mock_auth_user', JSON.stringify(mockUser));
    await ensureUserProfile(mockUser as unknown as User);
    window.dispatchEvent(new Event('mock-auth-change'));
    return mockUser as unknown as User;
  }

  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    // Update display name
    await updateProfile(result.user, { displayName: name });
    await ensureUserProfile(result.user);
    return result.user;
  } catch (error) {
    console.error("Registration Error", error);
    throw error;
  }
};

export const loginWithEmail = async (email: string, password: string) => {
  if (isMock) {
     console.log("Using Mock Auth (Login Email)");
     // Check if user exists in mock DB, otherwise create a session for them
     const existingUser = Object.values(mockDb.users).find(u => u.email === email);
     
     const mockUser = {
      uid: existingUser ? existingUser.uid : "mock-user-" + Math.floor(Math.random() * 10000),
      displayName: existingUser ? existingUser.displayName : "Returning User",
      email: email,
      photoURL: existingUser ? existingUser.photoURL : null,
      emailVerified: true
    };
    
    sessionStorage.setItem('mock_auth_user', JSON.stringify(mockUser));
    if (!existingUser) await ensureUserProfile(mockUser as unknown as User);
    
    window.dispatchEvent(new Event('mock-auth-change'));
    return mockUser as unknown as User;
  }

  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    // Profile should exist, but double check
    await ensureUserProfile(result.user);
    return result.user;
  } catch (error) {
    console.error("Login Error", error);
    throw error;
  }
};

export const logout = async () => {
  if (isMock) {
    sessionStorage.removeItem('mock_auth_user');
    window.dispatchEvent(new Event('mock-auth-change'));
    window.location.href = '/';
    return;
  }
  await signOut(auth);
  window.location.href = '/';
};

// --- ORGANIZATION SERVICES ---

const checkCodeExists = async (code: string): Promise<boolean> => {
  if (isMock) {
    return mockDb.organizations.some(o => o.code === code);
  }
  const q = query(collection(db, 'organizations'), where('code', '==', code));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

const generateUniqueOrgCode = async (name: string): Promise<string> => {
  // 1. Create base abbreviation
  const cleanName = name.replace(/[^a-zA-Z0-9\s]/g, '');
  const words = cleanName.trim().split(/\s+/);
  
  let base = "";
  if (words.length > 1) {
      // First letter of each word (up to 4 chars)
      base = words.map(w => w[0]).join('').substring(0, 5).toUpperCase();
  } else if (cleanName.length > 0) {
      // First 3 chars of single word
      base = cleanName.substring(0, 3).toUpperCase();
  } else {
      base = "ORG";
  }

  if (base.length < 3 && cleanName.length >= 3) {
      // Pad single word abbreviations that are too short if possible
      base = cleanName.substring(0, 3).toUpperCase();
  }

  // 2. Ensure uniqueness
  let code = base;
  let counter = 1;
  
  while (await checkCodeExists(code)) {
      code = `${base}${counter}`;
      counter++;
  }
  
  return code;
};

export const createOrganization = async (userId: string, name: string): Promise<Organization> => {
  const code = await generateUniqueOrgCode(name);
  
  if (isMock) {
    const newOrg: Organization = {
      id: "org-" + Date.now(),
      name,
      code,
      ownerId: userId,
      createdAt: Date.now(),
      adoptedStudyIds: []
    };
    mockDb.organizations.push(newOrg);
    
    // Update user to be admin of this org
    if (mockDb.users[userId]) {
      mockDb.users[userId].orgId = newOrg.id;
      mockDb.users[userId].isAdmin = true;
    }
    
    saveMockData();
    return newOrg;
  }

  // Real Firestore
  const newOrg: Organization = {
    id: '', // Will be set by Firestore
    name,
    code,
    ownerId: userId,
    createdAt: Date.now(),
    adoptedStudyIds: []
  };

  const orgRef = await addDoc(collection(db, 'organizations'), {
    name,
    code,
    ownerId: userId,
    createdAt: Date.now(),
    adoptedStudyIds: []
  });

  const orgWithId = { ...newOrg, id: orgRef.id };

  // Update User
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, { 
    orgId: orgRef.id,
    isAdmin: true 
  });

  return orgWithId;
};

export const joinOrganization = async (userId: string, code: string): Promise<Organization | null> => {
  const codeUpper = code.toUpperCase();
  
  if (isMock) {
    const org = mockDb.organizations.find(o => o.code === codeUpper);
    if (org && mockDb.users[userId]) {
      mockDb.users[userId].orgId = org.id;
      mockDb.users[userId].isAdmin = false; // Joiners are learners by default
      saveMockData();
      return org;
    }
    return null;
  }

  // Find Org by Code
  const q = query(collection(db, 'organizations'), where('code', '==', codeUpper));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return null;
  }

  const orgDoc = querySnapshot.docs[0];
  const org = { id: orgDoc.id, ...orgDoc.data() } as Organization;

  // Fetch user data first (for welcome email)
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  const userData = userSnap.exists() ? userSnap.data() as UserProfile : null;

  // Update User
  await updateDoc(userRef, {
    orgId: org.id,
    isAdmin: false
  });

  // Queue welcome email (only if user has email)
  if (userData?.email) {
    await queueEmail({
      type: 'welcome',
      userId,
      userEmail: userData.email,
      userName: userData.displayName || undefined,
      orgId: org.id
    });
  }

  return org;
};

export const searchOrganizations = async (searchTerm: string): Promise<Organization[]> => {
  if (isMock) {
    if (!searchTerm) {
      return mockDb.organizations.length <= 10 ? mockDb.organizations : [];
    }
    return mockDb.organizations.filter(o => 
      o.name.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 10);
  }

  if (!searchTerm) {
    // Check if we have few enough orgs to show them all
    const q = query(collection(db, 'organizations'), orderBy('name'), limit(11));
    const snapshot = await getDocs(q);
    const results: Organization[] = [];
    snapshot.forEach(doc => results.push({ id: doc.id, ...doc.data() } as Organization));
    
    // Only return if there are 10 or fewer total
    return results.length <= 10 ? results : [];
  }

  // Firestore prefix search (case-sensitive)
  const q = query(
    collection(db, 'organizations'),
    where('name', '>=', searchTerm),
    where('name', '<=', searchTerm + '\uf8ff'),
    orderBy('name'),
    limit(10)
  );
  
  const results: Organization[] = [];
  try {
    const snapshot = await getDocs(q);
    snapshot.forEach(doc => {
      results.push({ id: doc.id, ...doc.data() } as Organization);
    });
  } catch (e) {
    console.warn("Search query failed:", e);
  }

  return results;
};

export const getOrganization = async (orgId: string): Promise<Organization | null> => {
  if (isMock) {
    return mockDb.organizations.find(o => o.id === orgId) || null;
  }
  const docRef = doc(db, 'organizations', orgId);
  const snap = await getDoc(docRef);
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Organization) : null;
};

export const updateOrganization = async (orgId: string, data: Partial<Organization>): Promise<void> => {
  if (isMock) {
    const org = mockDb.organizations.find(o => o.id === orgId);
    if (org) {
      Object.assign(org, data);
      saveMockData();
    }
    return;
  }
  const docRef = doc(db, 'organizations', orgId);
  await updateDoc(docRef, data);
};

export const getOrgMembers = async (orgId: string): Promise<UserProfile[]> => {
  if (isMock) {
    return Object.values(mockDb.users).filter(u => u.orgId === orgId);
  }
  const q = query(collection(db, 'users'), where('orgId', '==', orgId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => doc.data() as UserProfile);
};

export const updateMemberRole = async (targetUid: string, isAdmin: boolean) => {
  if (isMock) {
    const user = mockDb.users[targetUid];
    if (user) {
      user.isAdmin = isAdmin;
      saveMockData();
    }
    return;
  }
  const userRef = doc(db, 'users', targetUid);
  await updateDoc(userRef, { isAdmin });
};

// --- STORAGE SERVICES ---

/**
 * Uploads a base64 image to Firebase Storage and returns the download URL
 * @param base64Data - The base64 data URL (e.g., "data:image/png;base64,...")
 * @param path - The storage path (e.g., "orgs/{orgId}/studies/{studyId}/covers/123.png")
 * @returns The download URL of the uploaded image
 */
export const uploadImageToStorage = async (base64Data: string, path: string): Promise<string> => {
  if (isMock) {
    // In mock mode, return the base64 data as-is
    return base64Data;
  }

  // Convert base64 to Blob
  const response = await fetch(base64Data);
  const blob = await response.blob();

  // Upload to Firebase Storage
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob);

  // Get and return download URL
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
};

export const uploadAudioToStorage = async (blob: Blob, path: string): Promise<string> => {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
};

export const uploadVideoToStorage = async (blob: Blob, path: string): Promise<string> => {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
};

// --- DATA SERVICES ---

const ensureUserProfile = async (user: User) => {
  if (isMock) {
    // Only update mockDb if it doesn't exist
    if (!mockDb.users[user.uid]) {
      const newUser: UserProfile = {
        uid: user.uid,
        displayName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
        completedStudyIds: [],
        studyProgress: {},
        awards: [],
        xp: 0,
        isAdmin: false
      };
      mockDb.users[user.uid] = newUser;
      saveMockData();
    }
    return;
  }

  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) {
    const newUser: UserProfile = {
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      photoURL: user.photoURL,
      completedStudyIds: [],
      studyProgress: {},
      awards: [],
      xp: 0,
      isAdmin: false
    };
    await setDoc(userRef, newUser);
  }
};

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  if (isMock) {
    return mockDb.users[uid] || null;
  }
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data() as UserProfile;
  }
  return null;
};

export const subscribeToUserProfile = (uid: string, callback: (profile: UserProfile | null) => void) => {
  if (isMock) {
    // Initial callback
    callback(mockDb.users[uid] || null);
    
    // Listen for mock changes
    const listener = () => {
      callback(mockDb.users[uid] || null);
    };
    window.addEventListener('mock-data-change', listener); // Assuming we emit this event on changes
    return () => window.removeEventListener('mock-data-change', listener);
  }

  const docRef = doc(db, 'users', uid);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data() as UserProfile);
    } else {
      callback(null);
    }
  });
};

export const toggleUserRole = async (uid: string) => {
  if (isMock) {
    const user = mockDb.users[uid];
    if (user) {
      user.isAdmin = !user.isAdmin;
      saveMockData();
    }
    return user?.isAdmin;
  }
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const current = docSnap.data().isAdmin;
    await updateDoc(docRef, { isAdmin: !current });
    return !current;
  }
  return false;
};

export const saveStudy = async (studyData: Omit<Study, 'id'>) => {
  if (isMock) {
    const newStudy: Study = {
      ...studyData,
      id: "mock-study-" + Math.random().toString(36).substr(2, 9)
    };
    mockDb.studies.push(newStudy);
    saveMockData();
    return newStudy.id;
  }
  const docRef = await addDoc(collection(db, 'studies'), studyData);
  return docRef.id;
};

export const updateStudy = async (id: string, data: Partial<Study>) => {
  if (isMock) {
    const idx = mockDb.studies.findIndex(s => s.id === id);
    if (idx > -1) {
      mockDb.studies[idx] = { ...mockDb.studies[idx], ...data };
      saveMockData();
    }
    return;
  }
  const docRef = doc(db, 'studies', id);
  await updateDoc(docRef, data);
};

export const deleteStudy = async (studyId: string) => {
  if (isMock) {
    const idx = mockDb.studies.findIndex(s => s.id === studyId);
    if (idx > -1) {
      mockDb.studies.splice(idx, 1);
      saveMockData();
    }
    return;
  }

  // Delete all questions associated with this study (cascade delete)
  const q = query(collection(db, 'questions'), where('studyId', '==', studyId));
  const snapshot = await getDocs(q);
  await Promise.all(snapshot.docs.map(doc => deleteDoc(doc.ref)));

  // Remove this study from any courses that reference it
  const coursesQuery = query(collection(db, 'courses'), where('studyIds', 'array-contains', studyId));
  const coursesSnapshot = await getDocs(coursesQuery);
  await Promise.all(coursesSnapshot.docs.map(courseDoc =>
    updateDoc(courseDoc.ref, { studyIds: arrayRemove(studyId) })
  ));

  // Delete the study
  await deleteDoc(doc(db, 'studies', studyId));
};

export const getOrgStudies = async (orgId: string): Promise<Study[]> => {
  if (isMock) {
    // Return all non-private studies belonging to this org (includes cloned studies)
    return mockDb.studies
      .filter(s => s.orgId === orgId && s.visibility !== 'private')
      .sort((a, b) => b.createdAt - a.createdAt);
  }

  // Fetch all non-private studies belonging to this org
  // This includes both originally created studies and cloned studies
  const q = query(
    collection(db, 'studies'),
    where('orgId', '==', orgId),
    where('visibility', 'in', ['org_only', 'all_orgs', 'public']),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Study));
};

export const getGlobalSharedStudies = async (): Promise<Study[]> => {
  if (isMock) {
    return mockDb.studies.filter(s => s.visibility === 'all_orgs' || s.visibility === 'public').sort((a,b) => b.createdAt - a.createdAt);
  }
  const q = query(
    collection(db, 'studies'),
    where('visibility', 'in', ['all_orgs', 'public']),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Study));
};

export const getPublicStudies = async (): Promise<Study[]> => {
  if (isMock) {
    return mockDb.studies.filter(s => s.visibility === 'public').sort((a,b) => b.createdAt - a.createdAt);
  }
  const q = query(
    collection(db, 'studies'),
    where('visibility', '==', 'public'),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Study));
};

export const getPublicCourses = async (): Promise<Course[]> => {
  if (isMock) {
    return mockDb.courses.filter(c => c.visibility === 'public').sort((a,b) => b.createdAt - a.createdAt);
  }
  const q = query(
    collection(db, 'courses'),
    where('visibility', '==', 'public'),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Course));
};

export const getCourseStudies = async (studyIds: string[]): Promise<Study[]> => {
  if (isMock) {
    return mockDb.studies.filter(s => studyIds.includes(s.id));
  }

  if (studyIds.length === 0) return [];

  // Check if user is authenticated
  const isAuthenticated = auth?.currentUser != null;

  if (!isAuthenticated) {
    // For unauthenticated users, fetch documents individually
    // This allows security rules to evaluate each document's visibility
    const results = await Promise.all(
      studyIds.map(async (studyId) => {
        try {
          const docRef = doc(db, 'studies', studyId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() } as Study;
          }
          return null;
        } catch (error) {
          // If permission denied, skip this study
          console.warn(`Could not fetch study ${studyId}:`, error);
          return null;
        }
      })
    );
    return results.filter((s): s is Study => s !== null);
  }

  // For authenticated users, try batch queries first (more efficient)
  // Fall back to individual fetches if batch fails (e.g. deleted/restricted studies)
  try {
    const batches: string[][] = [];
    for (let i = 0; i < studyIds.length; i += 10) {
      batches.push(studyIds.slice(i, i + 10));
    }

    const results = await Promise.all(
      batches.map(batch => {
        const q = query(
          collection(db, 'studies'),
          where(documentId(), 'in', batch)
        );
        return getDocs(q);
      })
    );

    return results.flatMap(snapshot =>
      snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Study))
    );
  } catch (error) {
    // Batch query failed (likely due to deleted or restricted studies)
    // Fall back to individual fetches with graceful error handling
    console.warn('Batch query failed, falling back to individual fetches:', error);
    const results = await Promise.all(
      studyIds.map(async (studyId) => {
        try {
          const docRef = doc(db, 'studies', studyId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() } as Study;
          }
          return null;
        } catch {
          console.warn(`Could not fetch study ${studyId}, skipping`);
          return null;
        }
      })
    );
    return results.filter((s): s is Study => s !== null);
  }
};

export const adoptStudy = async (orgId: string, studyId: string, authorId: string, authorName: string): Promise<string> => {
  if (isMock) {
    // Fetch the original study
    const originalStudy = mockDb.studies.find(s => s.id === studyId);
    if (!originalStudy) throw new Error('Study not found');

    // Create a clone with new ID and orgId, but preserve original author for reference
    const clonedStudy: Study = {
      ...originalStudy,
      id: `study_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      orgId,
      authorId, // Set the current admin as the author
      authorName, // Set the current admin's name
      visibility: 'org_only' as any, // Cloned studies default to org-only visibility
      clonedFrom: studyId,
      clonedAt: Date.now()
    };

    mockDb.studies.push(clonedStudy);
    saveMockData();
    return clonedStudy.id;
  }

  // Fetch the original study
  const studyRef = doc(db, 'studies', studyId);
  const studySnap = await getDoc(studyRef);
  if (!studySnap.exists()) throw new Error('Study not found');

  const originalStudy = { id: studySnap.id, ...studySnap.data() } as Study;

  // Create a clone with the adopting org's ID
  const clonedStudyData = {
    ...originalStudy,
    orgId,
    authorId, // Set the current admin as the author (owns the cloned copy)
    authorName, // Set the current admin's name
    visibility: 'org_only' as any, // Cloned studies default to org-only visibility
    clonedFrom: studyId,
    clonedAt: Date.now()
  };

  // Remove the id field before adding to Firestore (it will be auto-generated)
  const { id, ...studyDataWithoutId } = clonedStudyData;

  // Add the cloned study as a new document
  const newStudyRef = await addDoc(collection(db, 'studies'), studyDataWithoutId);
  return newStudyRef.id;
};

export const unadoptStudy = async (orgId: string, originalStudyId: string) => {
  if (isMock) {
    // Find the cloned study in this org
    const clonedStudy = mockDb.studies.find(
      s => s.orgId === orgId && s.clonedFrom === originalStudyId
    );
    if (clonedStudy) {
      mockDb.studies = mockDb.studies.filter(s => s.id !== clonedStudy.id);
      saveMockData();
    }
    return;
  }

  // Find the cloned study in this org
  const q = query(
    collection(db, 'studies'),
    where('orgId', '==', orgId),
    where('clonedFrom', '==', originalStudyId)
  );
  const querySnapshot = await getDocs(q);

  // Delete all matching cloned studies (should be max 1, but handle edge cases)
  const deletePromises = querySnapshot.docs.map(docSnap => deleteDoc(doc(db, 'studies', docSnap.id)));
  await Promise.all(deletePromises);
};

export const getUserStudies = async (uid: string): Promise<Study[]> => {
  if (isMock) {
    return mockDb.studies.filter(s => s.authorId === uid).sort((a, b) => b.createdAt - a.createdAt);
  }
  const q = query(
    collection(db, 'studies'), 
    where('authorId', '==', uid),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Study));
};

export const saveStudyProgress = async (userId: string, studyId: string, segmentIndex: number) => {
  if (isMock) {
    const user = mockDb.users[userId];
    if (user) {
      if (!user.studyProgress) user.studyProgress = {};
      user.studyProgress[studyId] = segmentIndex;
      saveMockData();
    }
    return;
  }

  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    const userData = userSnap.data() as UserProfile;
    const progress = userData.studyProgress || {};
    progress[studyId] = segmentIndex;
    await updateDoc(userRef, { studyProgress: progress });
  }
};

export const completeStudy = async (userId: string, studyId: string) => {
  if (isMock) {
    const user = mockDb.users[userId];
    if (!user) return null;

    // Initialize completedStudyIds if it doesn't exist
    if (!user.completedStudyIds) user.completedStudyIds = [];
    if (!user.awards) user.awards = [];

    if (!user.completedStudyIds.includes(studyId)) {
        const xpGain = 50;
        user.completedStudyIds.push(studyId);
        user.xp = (user.xp || 0) + xpGain;

        const completedCount = user.completedStudyIds.length;
        const newAwards: Award[] = [];

        if (completedCount === 1) {
            newAwards.push({
                id: 'first-step',
                name: 'First Step',
                description: 'Completed your first study',
                icon: 'footprints',
                earnedAt: Date.now()
            });
        } else if (completedCount === 5) {
             newAwards.push({
                id: 'devoted',
                name: 'Devoted Disciple',
                description: 'Completed 5 studies',
                icon: 'book-open',
                earnedAt: Date.now()
            });
        }

        if (newAwards.length > 0) {
            user.awards.push(...newAwards);
        }
        saveMockData();

        // Update streak and track completion
        await updateStreak(userId);
        await trackStudyCompletion(studyId);

        return { xpGain, newAwards };
    }
    return null;
  }

  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) return;

  const userData = userSnap.data() as UserProfile;

  // Initialize completedStudyIds if it doesn't exist
  const completedStudyIds = userData.completedStudyIds || [];

  if (completedStudyIds.includes(studyId)) {
    console.log('[completeStudy] Study already completed, skipping:', { userId, studyId });
    return null;
  }

  // Study not yet completed - process completion
  const xpGain = 50;

  // Manual array update since arrayUnion is not available in firestore/lite
  const updatedCompletedStudyIds = [...completedStudyIds, studyId];
  const updatedXp = (userData.xp || 0) + xpGain;

  const updates: any = {
    completedStudyIds: updatedCompletedStudyIds,
    xp: updatedXp
  };

  const completedCount = updatedCompletedStudyIds.length;
  const newAwards: Award[] = [];

  if (completedCount === 1) {
    newAwards.push({
      id: 'first-step',
      name: 'First Step',
      description: 'Completed your first study',
      icon: 'footprints',
      earnedAt: Date.now()
    });
  } else if (completedCount === 5) {
    newAwards.push({
      id: 'devoted',
      name: 'Devoted Disciple',
      description: 'Completed 5 studies',
      icon: 'book-open',
      earnedAt: Date.now()
    });
  }

  if (newAwards.length > 0) {
    updates.awards = [...(userData.awards || []), ...newAwards];
  }

  await updateDoc(userRef, updates);

  // Update streak and track completion
  await updateStreak(userId);
  await trackStudyCompletion(studyId);

  // Queue study completion email (only if user has org and email)
  if (userData.orgId && userData.email) {
    console.log('[Email Queue] Queuing study completion email for user:', userId, 'study:', studyId);
    await queueEmail({
      type: 'studyCompletion',
      userId,
      userEmail: userData.email,
      userName: userData.displayName || undefined,
      orgId: userData.orgId,
      studyId
    });
  } else {
    console.log('[Email Queue] Skipping study completion email - missing:', {
      hasOrgId: !!userData.orgId,
      hasEmail: !!userData.email,
      userId,
      studyId
    });
  }

  return { xpGain, newAwards };
}

// ===== HIGHLIGHTS & NOTES =====
export const saveHighlight = async (userId: string, studyId: string, highlight: import('../types').StudyHighlight) => {
  if (isMock) {
    const user = mockDb.users[userId];
    if (user) {
      if (!user.highlights) user.highlights = {};
      if (!user.highlights[studyId]) user.highlights[studyId] = [];
      user.highlights[studyId].push(highlight);
      saveMockData();
    }
    return;
  }

  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    const userData = userSnap.data();
    const highlights = userData.highlights || {};
    if (!highlights[studyId]) highlights[studyId] = [];
    highlights[studyId].push(highlight);
    await updateDoc(userRef, { highlights });
  }
};

// ===== READING PREFERENCES =====
export const saveReadingPreferences = async (userId: string, preferences: { fontSize: 'small' | 'medium' | 'large'; theme: 'light' | 'sepia' | 'dark' }) => {
  if (isMock) {
    const user = mockDb.users[userId];
    if (user) {
      user.readingPreferences = preferences;
      saveMockData();
    }
    return;
  }

  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, { readingPreferences: preferences });
};

// ===== STREAK TRACKING =====
export const updateStreak = async (userId: string) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTimestamp = today.getTime();

  if (isMock) {
    const user = mockDb.users[userId];
    if (user) {
      const lastStudy = user.lastStudyDate ? new Date(user.lastStudyDate) : null;
      if (lastStudy) lastStudy.setHours(0, 0, 0, 0);

      const studyDates = user.studyDates || [];

      // Don't count if already studied today
      if (lastStudy && lastStudy.getTime() === todayTimestamp) {
        return;
      }

      // Check if yesterday
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const isConsecutive = lastStudy && lastStudy.getTime() === yesterday.getTime();

      const currentStreak = isConsecutive ? (user.currentStreak || 0) + 1 : 1;
      const longestStreak = Math.max(user.longestStreak || 0, currentStreak);

      user.currentStreak = currentStreak;
      user.longestStreak = longestStreak;
      user.lastStudyDate = todayTimestamp;
      user.studyDates = [...studyDates, todayTimestamp];

      saveMockData();
    }
    return;
  }

  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const userData = userSnap.data();
    const lastStudy = userData.lastStudyDate ? new Date(userData.lastStudyDate) : null;
    if (lastStudy) lastStudy.setHours(0, 0, 0, 0);

    // Don't count if already studied today
    if (lastStudy && lastStudy.getTime() === todayTimestamp) {
      return;
    }

    const studyDates = userData.studyDates || [];

    // Check if yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const isConsecutive = lastStudy && lastStudy.getTime() === yesterday.getTime();

    const currentStreak = isConsecutive ? (userData.currentStreak || 0) + 1 : 1;
    const longestStreak = Math.max(userData.longestStreak || 0, currentStreak);

    await updateDoc(userRef, {
      currentStreak,
      longestStreak,
      lastStudyDate: todayTimestamp,
      studyDates: [...studyDates, todayTimestamp]
    });
  }
};

// ===== STUDY METRICS =====
export const trackStudyView = async (studyId: string, userId: string) => {
  if (isMock) {
    let metrics = mockDb.studyMetrics.find(m => m.studyId === studyId);
    if (!metrics) {
      metrics = {
        studyId,
        views: 0,
        completions: 0,
        uniqueViewers: [],
        lastUpdated: Date.now()
      };
      mockDb.studyMetrics.push(metrics);
    }
    metrics.views++;
    if (!metrics.uniqueViewers.includes(userId)) {
      metrics.uniqueViewers.push(userId);
    }
    metrics.lastUpdated = Date.now();
    saveMockData();
    return;
  }

  const metricsRef = doc(db, 'studyMetrics', studyId);
  const metricsSnap = await getDoc(metricsRef);

  if (metricsSnap.exists()) {
    const data = metricsSnap.data();
    const uniqueViewers = data.uniqueViewers || [];

    // If orgId is missing, fetch it from the study and add it
    const updateData: any = {
      views: (data.views || 0) + 1,
      uniqueViewers: uniqueViewers.includes(userId) ? uniqueViewers : [...uniqueViewers, userId],
      lastUpdated: Date.now()
    };

    if (!data.orgId) {
      const studyRef = doc(db, 'studies', studyId);
      const studySnap = await getDoc(studyRef);
      if (studySnap.exists()) {
        updateData.orgId = studySnap.data().orgId;
      }
    }

    await updateDoc(metricsRef, updateData);
  } else {
    // Create new metrics document - need to fetch study info for orgId
    const studyRef = doc(db, 'studies', studyId);
    const studySnap = await getDoc(studyRef);

    await setDoc(metricsRef, {
      studyId,
      views: 1,
      completions: 0,
      uniqueViewers: [userId],
      lastUpdated: Date.now(),
      // Store orgId for permission checks
      orgId: studySnap.exists() ? studySnap.data().orgId : ''
    });
  }
};

export const trackStudyCompletion = async (studyId: string) => {
  if (isMock) {
    let metrics = mockDb.studyMetrics.find(m => m.studyId === studyId);
    if (!metrics) {
      metrics = {
        studyId,
        views: 0,
        completions: 0,
        uniqueViewers: [],
        lastUpdated: Date.now()
      };
      mockDb.studyMetrics.push(metrics);
    }
    metrics.completions++;
    metrics.lastUpdated = Date.now();
    saveMockData();
    return;
  }

  const metricsRef = doc(db, 'studyMetrics', studyId);
  const metricsSnap = await getDoc(metricsRef);

  if (metricsSnap.exists()) {
    const data = metricsSnap.data();

    // Prepare update data
    const updateData: any = {
      completions: (data.completions || 0) + 1,
      lastUpdated: Date.now()
    };

    // If orgId is missing, fetch it from the study and add it
    if (!data.orgId) {
      const studyRef = doc(db, 'studies', studyId);
      const studySnap = await getDoc(studyRef);
      if (studySnap.exists()) {
        updateData.orgId = studySnap.data().orgId;
      }
    }

    await updateDoc(metricsRef, updateData);
  } else {
    // Create new metrics document if it doesn't exist
    const studyRef = doc(db, 'studies', studyId);
    const studySnap = await getDoc(studyRef);

    await setDoc(metricsRef, {
      studyId,
      views: 0,
      completions: 1,
      uniqueViewers: [],
      lastUpdated: Date.now(),
      orgId: studySnap.exists() ? studySnap.data().orgId : ''
    });
  }
};

export const getStudyMetrics = async (studyId: string): Promise<import('../types').StudyMetrics | null> => {
  if (isMock) {
    const metrics = mockDb.studyMetrics.find(m => m.studyId === studyId);
    return metrics || null;
  }

  const metricsRef = doc(db, 'studyMetrics', studyId);
  const metricsSnap = await getDoc(metricsRef);

  if (metricsSnap.exists()) {
    return metricsSnap.data() as import('../types').StudyMetrics;
  }
  return null;
};

// ========== COURSE FUNCTIONS ==========

export const saveCourse = async (courseData: Omit<Course, 'id'>) => {
  if (isMock) {
    const newCourse: Course = {
      ...courseData,
      id: "mock-course-" + Math.random().toString(36).substr(2, 9)
    };
    mockDb.courses.push(newCourse);
    saveMockData();
    return newCourse.id;
  }
  const docRef = await addDoc(collection(db, 'courses'), courseData);
  return docRef.id;
};

export const updateCourse = async (id: string, data: Partial<Course>) => {
  if (isMock) {
    const idx = mockDb.courses.findIndex(p => p.id === id);
    if (idx > -1) {
      mockDb.courses[idx] = { ...mockDb.courses[idx], ...data };
      saveMockData();
    }
    return;
  }
  const docRef = doc(db, 'courses', id);
  await updateDoc(docRef, data);
};

export const deleteCourse = async (courseId: string) => {
  if (isMock) {
    const idx = mockDb.courses.findIndex(p => p.id === courseId);
    if (idx > -1) {
      mockDb.courses.splice(idx, 1);
      saveMockData();
    }
    return;
  }
  await deleteDoc(doc(db, 'courses', courseId));
};

export const getOrgCourses = async (orgId: string): Promise<Course[]> => {
  if (isMock) {
    return mockDb.courses
      .filter(p => p.orgId === orgId && p.visibility !== 'private')
      .sort((a, b) => b.createdAt - a.createdAt);
  }

  const q = query(
    collection(db, 'courses'),
    where('orgId', '==', orgId),
    where('visibility', 'in', ['org_only', 'all_orgs', 'public']),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Course));
};

export const getCourseById = async (courseId: string): Promise<Course | null> => {
  if (isMock) {
    return mockDb.courses.find(p => p.id === courseId) || null;
  }

  const docRef = doc(db, 'courses', courseId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as Course;
  }
  return null;
};

export const getStudyById = async (studyId: string): Promise<Study | null> => {
  if (isMock) {
    return mockDb.studies.find(s => s.id === studyId) || null;
  }

  const docRef = doc(db, 'studies', studyId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as Study;
  }
  return null;
};

export const saveCourseProgress = async (userId: string, courseId: string, studyIndex: number) => {
  if (isMock) {
    const user = mockDb.users[userId];
    if (user) {
      if (!user.courseProgress) user.courseProgress = {};
      user.courseProgress[courseId] = studyIndex;
      saveMockData();
    }
    return;
  }

  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    [`courseProgress.${courseId}`]: studyIndex
  });
};

export const completeCourse = async (userId: string, courseId: string) => {
  if (isMock) {
    const user = mockDb.users[userId];
    if (user) {
      if (!user.completedCourseIds) user.completedCourseIds = [];
      if (!user.completedCourseIds.includes(courseId)) {
        user.completedCourseIds.push(courseId);
        // Award XP for completing a course (100 XP)
        user.xp = (user.xp || 0) + 100;
      }
      saveMockData();
    }
    return;
  }

  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const userData = userSnap.data() as UserProfile;
    const completedCourses = userData.completedCourseIds || [];

    if (!completedCourses.includes(courseId)) {
      await updateDoc(userRef, {
        completedCourseIds: [...completedCourses, courseId],
        xp: (userData.xp || 0) + 100
      });

      // Queue course completion email (only if user has org and email)
      if (userData.orgId && userData.email) {
        await queueEmail({
          type: 'courseCompletion',
          userId,
          userEmail: userData.email,
          userName: userData.displayName || undefined,
          orgId: userData.orgId,
          courseId
        });
      }
    }
  }
};

export const getCourseMetrics = async (courseId: string): Promise<CourseMetrics | null> => {
  if (isMock) {
    const metrics = mockDb.courseMetrics.find(m => m.courseId === courseId);
    return metrics || null;
  }

  const metricsRef = doc(db, 'courseMetrics', courseId);
  const metricsSnap = await getDoc(metricsRef);

  if (metricsSnap.exists()) {
    return metricsSnap.data() as CourseMetrics;
  }
  return null;
};

// ===== QUESTION MANAGEMENT =====

export const submitQuestion = async (
  studyId: string,
  segmentId: string,
  userId: string,
  userEmail: string | null,
  userName: string | null,
  questionText: string
): Promise<string> => {
  if (isMock) {
    return `mock-question-${Date.now()}`;
  }

  const docRef = await addDoc(collection(db, 'questions'), {
    studyId,
    segmentId,
    userId,
    userEmail,
    userName,
    questionText,
    createdAt: Date.now(),
    replies: [],
    isRead: false
  });
  return docRef.id;
};

export const getStudyQuestions = async (studyId: string): Promise<Question[]> => {
  if (isMock) return [];

  const q = query(
    collection(db, 'questions'),
    where('studyId', '==', studyId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Question));
};

export const getSegmentQuestions = async (
  studyId: string,
  segmentId: string
): Promise<Question[]> => {
  if (isMock) return [];

  const q = query(
    collection(db, 'questions'),
    where('studyId', '==', studyId),
    where('segmentId', '==', segmentId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Question));
};

export const getUserQuestions = async (
  userId: string,
  studyId?: string
): Promise<Question[]> => {
  if (isMock) return [];

  let q;
  if (studyId) {
    // Get questions for a specific study
    q = query(
      collection(db, 'questions'),
      where('userId', '==', userId),
      where('studyId', '==', studyId),
      orderBy('createdAt', 'desc')
    );
  } else {
    // Get all questions for the user across all studies
    q = query(
      collection(db, 'questions'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
  }
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Question));
};

export const addQuestionReply = async (
  questionId: string,
  authorId: string,
  authorName: string,
  replyText: string
): Promise<void> => {
  if (isMock) return;

  const questionRef = doc(db, 'questions', questionId);
  const questionSnap = await getDoc(questionRef);
  if (!questionSnap.exists()) throw new Error('Question not found');

  const existingReplies = questionSnap.data().replies || [];
  await updateDoc(questionRef, {
    replies: [...existingReplies, {
      id: `reply-${Date.now()}`,
      authorId,
      authorName,
      replyText,
      createdAt: Date.now()
    }],
    isRead: true
  });
};

export const markQuestionAsRead = async (questionId: string): Promise<void> => {
  if (isMock) return;
  await updateDoc(doc(db, 'questions', questionId), { isRead: true });
};

export const deleteQuestion = async (questionId: string): Promise<void> => {
  if (isMock) return;
  await deleteDoc(doc(db, 'questions', questionId));
};

export const getUnreadQuestionCount = async (studyId: string): Promise<number> => {
  if (isMock) return 0;

  const q = query(
    collection(db, 'questions'),
    where('studyId', '==', studyId),
    where('isRead', '==', false)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.length;
};

// ===== MEDIA MANAGEMENT =====

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
const removeUndefinedFields = (obj: Record<string, any>): Record<string, any> => {
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

/**
 * Save a media item to Firestore
 */
export const saveMediaItem = async (mediaData: Omit<MediaItem, 'id'>): Promise<string> => {
  if (isMock) {
    const id = `media-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return id;
  }

  // Remove undefined fields before saving (Firestore doesn't accept undefined)
  const cleanedData = removeUndefinedFields(mediaData as Record<string, any>);
  const docRef = await addDoc(collection(db, 'orgMedia'), cleanedData);
  return docRef.id;
};

/**
 * Get all media for an organization
 */
export const getOrgMedia = async (
  orgId: string,
  mediaType?: MediaType,
  searchQuery?: string
): Promise<MediaItem[]> => {
  if (isMock) {
    return [];
  }

  let q;
  if (mediaType) {
    q = query(
      collection(db, 'orgMedia'),
      where('orgId', '==', orgId),
      where('mediaType', '==', mediaType),
      orderBy('uploadedAt', 'desc')
    );
  } else {
    q = query(
      collection(db, 'orgMedia'),
      where('orgId', '==', orgId),
      orderBy('uploadedAt', 'desc')
    );
  }

  const snapshot = await getDocs(q);
  let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MediaItem));

  // Client-side filtering for search (Firestore doesn't support text search)
  if (searchQuery) {
    const queryLower = searchQuery.toLowerCase();
    results = results.filter(item =>
      item.prompt?.toLowerCase().includes(queryLower) ||
      item.fileName?.toLowerCase().includes(queryLower)
    );
  }

  return results;
};

/**
 * Get a single media item by ID
 */
export const getMediaItem = async (mediaId: string): Promise<MediaItem | null> => {
  if (isMock) return null;

  const docRef = doc(db, 'orgMedia', mediaId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as MediaItem;
  }
  return null;
};

/**
 * Delete a media item (from both Firestore and Storage)
 */
export const deleteMediaItem = async (mediaId: string, storagePath: string): Promise<void> => {
  if (isMock) return;

  // Delete from Storage first
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.warn('Failed to delete file from storage:', error);
    // Continue to delete Firestore record even if storage deletion fails
  }

  // Delete from Firestore
  await deleteDoc(doc(db, 'orgMedia', mediaId));
};

/**
 * Get images only for media picker
 */
export const getOrgImages = async (orgId: string): Promise<MediaItem[]> => {
  return getOrgMedia(orgId, 'image');
};

// ===== CTA (CALL TO ACTION) FUNCTIONS =====

/**
 * Save a CTA response to Firestore
 * CTAs are now segments within studies (not course-level items)
 */
export const saveCTAResponse = async (
  orgId: string,
  studyId: string,
  segmentId: string,
  ctaType: CTAType,
  userId: string | null,
  response: CTAResponseData
): Promise<string> => {
  if (isMock) {
    return `mock-cta-response-${Date.now()}`;
  }

  // Filter out undefined values from response (Firestore doesn't accept undefined)
  const cleanResponse = Object.fromEntries(
    Object.entries(response).filter(([_, v]) => v !== undefined)
  );

  const docRef = await addDoc(collection(db, 'ctaResponses'), {
    orgId,
    studyId,
    segmentId,
    ctaType,
    userId: userId || null,
    respondedAt: Date.now(),
    response: cleanResponse
  });
  return docRef.id;
};

/**
 * Get CTA responses for admin view
 */
export const getCTAResponses = async (
  orgId: string,
  studyId?: string,
  segmentId?: string
): Promise<CTAResponse[]> => {
  if (isMock) return [];

  let q = query(
    collection(db, 'ctaResponses'),
    where('orgId', '==', orgId),
    orderBy('respondedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  let responses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CTAResponse));

  // Filter by studyId and segmentId if provided
  if (studyId) {
    responses = responses.filter(r => r.studyId === studyId);
  }
  if (segmentId) {
    responses = responses.filter(r => r.segmentId === segmentId);
  }

  return responses;
};

/**
 * Get count of CTA responses for a specific CTA segment
 */
export const getCTAResponseCount = async (
  orgId: string,
  studyId: string,
  segmentId: string
): Promise<number> => {
  if (isMock) return 0;

  const q = query(
    collection(db, 'ctaResponses'),
    where('orgId', '==', orgId),
    where('studyId', '==', studyId),
    where('segmentId', '==', segmentId)
  );
  const snapshot = await getDocs(q);
  return snapshot.size;
};

/**
 * Save user's CTA interaction (skip or complete)
 * CTAs are now segments within studies
 */
export const saveCTAInteraction = async (
  userId: string,
  studyId: string,
  segmentId: string,
  status: 'skipped' | 'completed',
  responseId?: string
): Promise<void> => {
  if (isMock) {
    const user = mockDb.users[userId];
    if (user) {
      if (!user.ctaInteractions) user.ctaInteractions = {};
      if (!user.ctaInteractions[studyId]) user.ctaInteractions[studyId] = {};
      user.ctaInteractions[studyId][segmentId] = {
        status,
        timestamp: Date.now(),
        responseId
      };
      saveMockData();
    }
    return;
  }

  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    [`ctaInteractions.${studyId}.${segmentId}`]: {
      status,
      timestamp: Date.now(),
      responseId: responseId || null
    }
  });
};

/**
 * Get user's CTA interaction status
 */
export const getUserCTAInteraction = async (
  userId: string,
  studyId: string,
  segmentId: string
): Promise<CTAInteraction | null> => {
  if (isMock) {
    const user = mockDb.users[userId];
    return user?.ctaInteractions?.[studyId]?.[segmentId] || null;
  }

  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const userData = userSnap.data() as UserProfile;
    return userData.ctaInteractions?.[studyId]?.[segmentId] || null;
  }
  return null;
};